import { useState } from 'react';
import type { GenUiDispatch } from '../../../shared/ui/action.js';
import type {
  BusSeatLegSelection,
  BusSeatMapBlock,
  ChatUiBusSeat,
  ChatUiBusSeatLeg,
} from '@voll-intelligence/genui-contract';
import { ArrowRightIcon, SeatIcon } from '@phosphor-icons/react';
import { useGenUiI18n } from '../../../shared/ui/i18n.js';
import { CardFooter } from '../../../shared/ui/card.js';
import './bus_seat_map.css';

/**
 * Mapa de poltronas de ônibus (`bus_seat_map`) — grade de assentos por TRECHO. Em
 * round-trip o card mostra IDA e VOLTA como duas seções (cada uma com sua grade OU
 * o estado "sem assento" se aquele trecho falhou); one-way é uma seção só. O
 * cliente resolve CADA trecho (toca numa poltrona livre OU "seguir sem assento") e
 * um único **Continuar** ao fim devolve a escolha COMBINADA por `interaction`
 * (challengeId + `{ seats: { outbound, return? } }` ESTRUTURADO) — o `seatCode`
 * nunca vira texto livre do LLM. Congela ao resolver (`readOnly`). Mirror do
 * painel de tarifas do `flight_offers` (uma coluna por trecho, um CTA só).
 */
export function BusSeatMap({
  ui,
  note,
  readOnly = false,
  dispatch,
}: {
  readonly ui: BusSeatMapBlock;
  readonly note?: string;
  readonly readOnly?: boolean;
  readonly dispatch: GenUiDispatch;
}) {
  const { t } = useGenUiI18n();
  // Resolução por trecho: poltrona escolhida (`{ seatCode }`) OU "seguir sem
  // assento" (`{ noSeat: true }`). `undefined` = trecho ainda não resolvido.
  const [picks, setPicks] = useState<Partial<Record<'outbound' | 'return', BusSeatLegSelection>>>(
    {},
  );

  const pickSeat = (leg: 'outbound' | 'return', seat: ChatUiBusSeat) => {
    if (seat.occupied) return;
    setPicks((prev) => ({ ...prev, [leg]: { seatCode: seat.code } }));
  };

  // "Seguir sem assento" para UM trecho: a equipe VOLL escolhe (o booking omite
  // travelSeats desse trecho). Disponível como saída no estado de erro E como ação
  // secundária na grade normal daquele trecho.
  const proceedWithoutSeat = (leg: 'outbound' | 'return') => {
    setPicks((prev) => ({ ...prev, [leg]: { noSeat: true } }));
  };

  // "Escolher outra viagem": texto livre que faz o LLM refazer a busca — não
  // resolve o challenge (não é uma seleção estruturada).
  const chooseOtherTrip = () => {
    dispatch({ type: 'reply', text: t('busSeatMap.otherTripText') });
  };

  // Continuar habilita quando CADA trecho tem uma resolução (poltrona OU sem
  // assento). Ao clicar, dispara UMA interação com a escolha combinada dos trechos.
  const allResolved = ui.legs.every((l) => picks[l.leg] !== undefined);
  const outboundPick = picks.outbound;
  const hasReturn = ui.legs.some((l) => l.leg === 'return');

  const continueSelection = () => {
    if (readOnly || !allResolved || !outboundPick) return;
    const returnPick = picks.return;
    dispatch({
      type: 'interaction',
      interactionId: ui.challengeId,
      text: summarizeText(t, outboundPick, hasReturn ? returnPick : undefined),
      data: {
        seats: {
          outbound: outboundPick,
          ...(hasReturn && returnPick ? { return: returnPick } : {}),
        },
      },
    });
  };

  return (
    <div className="bsm" aria-label={t('busSeatMap.aria')}>
      <div className="bsm-body">
        {ui.legs.map((leg) => (
          <LegSection
            key={leg.leg}
            leg={leg}
            multiLeg={ui.legs.length > 1}
            pick={picks[leg.leg]}
            readOnly={readOnly}
            onPickSeat={(seat) => {
              pickSeat(leg.leg, seat);
            }}
            onProceedWithoutSeat={() => {
              proceedWithoutSeat(leg.leg);
            }}
            onChooseOtherTrip={chooseOtherTrip}
          />
        ))}
      </div>

      {!readOnly && (
        <div className="bsm-footer-actions">
          <button
            type="button"
            className="bsm-btn bsm-btn-primary bsm-continue"
            disabled={!allResolved}
            onClick={continueSelection}
          >
            {t('busSeatMap.continue')}
          </button>
        </div>
      )}

      <CardFooter note={note} />
    </div>
  );
}

/**
 * Rótulo curto do resumo da interação (não é wire — só o texto do turno). One-way
 * é uma frase completa ("Quero a poltrona 56."); round-trip vira um resumo rotulado
 * por trecho ("Ida: poltrona 56 · Volta: poltrona 59.") pra não repetir a frase.
 */
function summarizeText(
  t: (
    k:
      | 'busSeatMap.picked'
      | 'busSeatMap.noSeatPicked'
      | 'busSeatMap.pickedSeat'
      | 'busSeatMap.pickedNoSeat'
      | 'busSeatMap.pickedRound',
    v?: Record<string, string>,
  ) => string,
  outbound: BusSeatLegSelection,
  ret: BusSeatLegSelection | undefined,
): string {
  if (!ret) {
    return outbound.noSeat
      ? t('busSeatMap.noSeatPicked')
      : t('busSeatMap.picked', { code: outbound.seatCode });
  }
  const frag = (s: BusSeatLegSelection) =>
    s.noSeat ? t('busSeatMap.pickedNoSeat') : t('busSeatMap.pickedSeat', { code: s.seatCode });
  return t('busSeatMap.pickedRound', { outbound: frag(outbound), return: frag(ret) });
}

/** Uma seção do card = um trecho (IDA ou VOLTA): cabeçalho + grade OU erro. */
function LegSection({
  leg,
  multiLeg,
  pick,
  readOnly,
  onPickSeat,
  onProceedWithoutSeat,
  onChooseOtherTrip,
}: {
  readonly leg: ChatUiBusSeatLeg;
  readonly multiLeg: boolean;
  readonly pick: BusSeatLegSelection | undefined;
  readonly readOnly: boolean;
  readonly onPickSeat: (seat: ChatUiBusSeat) => void;
  readonly onProceedWithoutSeat: () => void;
  readonly onChooseOtherTrip: () => void;
}) {
  const { t } = useGenUiI18n();
  const noSeatPicked = pick?.noSeat === true;
  const selectedCode = pick && !pick.noSeat ? pick.seatCode : null;

  let titleText = t('busSeatMap.title');
  if (multiLeg) {
    titleText = leg.leg === 'outbound' ? t('busSeatMap.legOutbound') : t('busSeatMap.legReturn');
  }

  const header = (
    <div className="bsm-head">
      <span className="bsm-ic">
        <SeatIcon size={20} aria-hidden />
      </span>
      <div className="bsm-htx">
        <span className="bsm-ttl">{titleText}</span>
        <span className="bsm-sub">
          {leg.trip.carrierName} · {leg.trip.from} <ArrowRightIcon size={12} aria-hidden />{' '}
          {leg.trip.to}
        </span>
      </div>
    </div>
  );

  // Estado de erro deste trecho: a consulta de assentos falhou — a seção NÃO mostra
  // a grade; mostra a mensagem + "seguir sem assento" / "escolher outra viagem".
  if (leg.seatError) {
    return (
      <div className="bsm-section bsm-error">
        {header}
        <p className="bsm-errmsg">{t('busSeatMap.errorTitle')}</p>
        <div className="bsm-actions">
          <button
            type="button"
            className="bsm-btn bsm-btn-secondary"
            disabled={readOnly || noSeatPicked}
            onClick={onProceedWithoutSeat}
          >
            {noSeatPicked ? t('busSeatMap.noSeatSelected') : t('busSeatMap.noSeat')}
          </button>
          <button
            type="button"
            className="bsm-btn bsm-btn-ghost"
            disabled={readOnly}
            onClick={onChooseOtherTrip}
          >
            {t('busSeatMap.otherTrip')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bsm-section">
      {header}

      <div className="bsm-grid">
        {leg.floors.map((floor, fi) => (
          // `rowNumber` não é único ENTRE andares (o schema não exige): num leito os
          // dois andares podem começar na fileira 1, então o índice entra na chave.
          <div
            className="bsm-floor"
            key={`floor-${String(fi)}-${String(floor.rows[0]?.rowNumber ?? '')}`}
          >
            {/* Rótulo do andar só quando o ônibus é de dois andares (leito). */}
            {leg.floors.length > 1 && (
              <span className="bsm-floor-label">
                {t('busSeatMap.floor', { n: String(fi + 1) })}
              </span>
            )}
            {/* Indicador da frente do ônibus (volante) no topo de cada andar. */}
            <div className="bsm-front" aria-hidden="true">
              🚍 {t('busSeatMap.front')}
            </div>
            {floor.rows.map((row) => (
              <div className="bsm-row" key={row.rowNumber}>
                <span className="bsm-rownum">{row.rowNumber}</span>
                <SeatRow
                  seats={row.seats}
                  selectedCode={selectedCode}
                  readOnly={readOnly}
                  onPickSeat={onPickSeat}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      <div className="bsm-legend">
        <span className="bsm-leg">
          <span className="bsm-dot bsm-dot-free" /> {t('busSeatMap.legendFree')}
        </span>
        <span className="bsm-leg">
          <span className="bsm-dot bsm-dot-occupied" /> {t('busSeatMap.legendOccupied')}
        </span>
        <span className="bsm-leg">
          <span className="bsm-dot bsm-dot-sel" /> {t('busSeatMap.legendSelected')}
        </span>
      </div>

      {/* Ação secundária deste trecho: quem não se importa com a poltrona pula
          direto — a equipe VOLL escolhe (booking omite travelSeats desse trecho). */}
      <button
        type="button"
        className="bsm-btn bsm-btn-ghost bsm-skip"
        disabled={readOnly || selectedCode !== null}
        title={t('busSeatMap.noSeatHint')}
        onClick={onProceedWithoutSeat}
      >
        {noSeatPicked ? t('busSeatMap.noSeatSelected') : t('busSeatMap.noSeat')}
      </button>
    </div>
  );
}

/**
 * Uma fileira renderizada como PLANTA do ônibus: as poltronas ficam nos seus
 * lados reais (colunas 1/2 à esquerda · corredor · colunas 4/5 à direita — 2+2),
 * com o corredor visível como um GAP (`.bsm-aisle`), não como poltrona. O 2+1
 * (leito-cama) cai naturalmente das colunas presentes. Sem `column` (fallback) as
 * poltronas seguem sequenciais num grupo só, sem corredor.
 */
function SeatRow({
  seats,
  selectedCode,
  readOnly,
  onPickSeat,
}: {
  readonly seats: ChatUiBusSeat[];
  readonly selectedCode: string | null;
  readonly readOnly: boolean;
  readonly onPickSeat: (seat: ChatUiBusSeat) => void;
}) {
  // Corredor = gap entre a coluna 2 (fim do lado esquerdo) e a 4 (início do
  // direito). Sem nenhuma `column`, tudo cai à esquerda e o corredor some.
  const hasColumns = seats.some((s) => s.column != null);
  const left = hasColumns ? seats.filter((s) => (s.column ?? 1) <= 2) : seats;
  const right = hasColumns ? seats.filter((s) => (s.column ?? 1) >= 4) : [];
  // A `column` decide só o LADO (esquerdo/direito do corredor); DENTRO de cada
  // lado a ordem segue o número da poltrona, ascendente — a numeração do ônibus
  // (e o app de referência) lê esquerda→direita crescente. O upstream às vezes
  // devolve o lado direito invertido (p4=44, p5=43), então ordenar por `column`
  // mostraria "44 43"; ordenar por `code` (numérico) devolve "43 44".
  const byCode = (a: ChatUiBusSeat, b: ChatUiBusSeat) =>
    a.code.localeCompare(b.code, undefined, { numeric: true });
  const renderSeat = (seat: ChatUiBusSeat) => (
    <Seat
      key={seat.code}
      seat={seat}
      selected={selectedCode === seat.code}
      readOnly={readOnly}
      onPickSeat={onPickSeat}
    />
  );
  return (
    <div className="bsm-seats">
      <div className="bsm-col-group">{[...left].sort(byCode).map(renderSeat)}</div>
      {right.length > 0 && (
        <>
          <span className="bsm-aisle" aria-hidden="true" />
          <div className="bsm-col-group">{[...right].sort(byCode).map(renderSeat)}</div>
        </>
      )}
    </div>
  );
}

/** Uma poltrona clicável (livre/ocupada/selecionada). */
function Seat({
  seat,
  selected,
  readOnly,
  onPickSeat,
}: {
  readonly seat: ChatUiBusSeat;
  readonly selected: boolean;
  readonly readOnly: boolean;
  readonly onPickSeat: (seat: ChatUiBusSeat) => void;
}) {
  const { t } = useGenUiI18n();
  return (
    <button
      type="button"
      className={`bsm-seat${seat.occupied ? ' is-occupied' : ''}${selected ? ' is-sel' : ''}`}
      disabled={seat.occupied || readOnly}
      aria-label={
        seat.occupied
          ? t('busSeatMap.occupied', { code: seat.code })
          : t('busSeatMap.free', { code: seat.code })
      }
      onClick={() => {
        onPickSeat(seat);
      }}
    >
      {seat.code}
    </button>
  );
}
